// ============================================================
// Client-side road queries for Curse of the Portal placement.
// Roads come straight from the vector tiles we already render
// (OpenMapTiles `transportation` layer) — no server road network.
// ============================================================
import {
  bearing as turfBearing,
  booleanPointInPolygon,
  destination,
  distance,
  lineIntersect,
  lineString,
  nearestPointOnLine,
  point,
  polygon as turfPolygon,
} from '@turf/turf';
import type { Map as MlMap } from 'maplibre-gl';
import type { Feature, LineString, MultiLineString, Polygon } from 'geojson';
import type { Boundary, LngLat } from '../../shared/protocol';
import { ft, METERS_PER_FOOT } from '../../shared/constants';

type RoadFeature = Feature<LineString | MultiLineString>;
const EXCLUDED = new Set(['ferry', 'path', 'steps', 'track']);

/** Roads currently in the loaded vector tiles (driveable/walkable lines). */
export function queryRoads(map: MlMap): RoadFeature[] {
  // v9 (BOLT-014): the keyless fallback style has no vector tiles at all. Its
  // contract is that the app stays fully exercisable without imagery (dev/CI),
  // so extend it with a synthetic street lattice — otherwise portal placement
  // ("on a road") is impossible keyless. Keyed maps never take this branch.
  if (!map.getSource('omt')) return syntheticGrid(map);
  let feats: GeoJSON.Feature[] = [];
  try {
    feats = map.querySourceFeatures('omt', { sourceLayer: 'transportation' });
  } catch {
    return [];
  }
  const out: RoadFeature[] = [];
  const seen = new Set<string>();
  for (const f of feats) {
    const g = f.geometry;
    if (!g || (g.type !== 'LineString' && g.type !== 'MultiLineString')) continue;
    if (EXCLUDED.has((f.properties?.class as string) ?? '')) continue;
    const key = JSON.stringify(g.coordinates);
    if (seen.has(key)) continue; // tiles can duplicate features
    seen.add(key);
    out.push({ type: 'Feature', properties: {}, geometry: g as LineString | MultiLineString });
  }
  return out;
}

/** Keyless-mode stand-in streets: a ~300 ft lattice across the viewport, so
 *  every placement rule (snap, keepout, exit band) exercises identically in
 *  CI and keyless dev. Never rendered — placement geometry only. */
function syntheticGrid(map: MlMap): RoadFeature[] {
  const b = map.getBounds();
  const south = b.getSouth();
  const north = b.getNorth();
  const west = b.getWest();
  const east = b.getEast();
  const stepLat = ft(300) / 111_320;
  const stepLng = stepLat / Math.cos((((south + north) / 2) * Math.PI) / 180);
  const out: RoadFeature[] = [];
  const cap = 60; // zoomed way out, stay bounded
  for (let lat = south, i = 0; lat <= north && i < cap; lat += stepLat, i++) {
    out.push(lineString([
      [west, lat],
      [east, lat],
    ]) as RoadFeature);
  }
  for (let lng = west, i = 0; lng <= east && i < cap; lng += stepLng, i++) {
    out.push(lineString([
      [lng, south],
      [lng, north],
    ]) as RoadFeature);
  }
  return out;
}

/** Nearest point on any road to `p`, with its distance in metres. */
export function nearestRoadPoint(roads: RoadFeature[], p: LngLat): { at: LngLat; distM: number } | null {
  const pt = point([p.lng, p.lat]);
  let best: { at: LngLat; distM: number } | null = null;
  for (const road of roads) {
    try {
      const np = nearestPointOnLine(road, pt, { units: 'meters' });
      const d = np.properties.dist as number;
      if (!best || d < best.distM) {
        const [lng, lat] = np.geometry.coordinates;
        best = { at: { lat, lng }, distM: d };
      }
    } catch {
      /* skip degenerate */
    }
  }
  return best;
}

export const isOnRoad = (roads: RoadFeature[], p: LngLat, tolFt = 40): boolean => {
  const n = nearestRoadPoint(roads, p);
  return !!n && n.distM <= ft(tolFt);
};

/** Axis-aligned square of `sizeFt` centred on `c`. */
export function boxAround(c: LngLat, sizeFt: number): Feature<Polygon> {
  const half = ft(sizeFt) / 2;
  const dLat = half / 111_320;
  const dLng = half / (111_320 * Math.cos((c.lat * Math.PI) / 180));
  return turfPolygon([
    [
      [c.lng - dLng, c.lat - dLat],
      [c.lng + dLng, c.lat - dLat],
      [c.lng + dLng, c.lat + dLat],
      [c.lng - dLng, c.lat + dLat],
      [c.lng - dLng, c.lat - dLat],
    ],
  ]);
}

export function pointInBox(p: LngLat, c: LngLat, sizeFt: number): boolean {
  const half = ft(sizeFt) / 2;
  const dLat = half / 111_320;
  const dLng = half / (111_320 * Math.cos((c.lat * Math.PI) / 180));
  return Math.abs(p.lat - c.lat) <= dLat && Math.abs(p.lng - c.lng) <= dLng;
}

/**
 * Auto-place the exit: ray hider → entry → outward; first road crossing ≥ minFt
 * from the entry, on the far side from the hider. If that's out of bounds (or
 * none), snap to the nearest in-bounds road point where the ray leaves the area.
 * Always returns an in-bounds point (so the seeker can never be soft-locked).
 */
export function computeExit(
  hider: LngLat,
  entry: LngLat,
  roads: RoadFeature[],
  boundary: Boundary,
  minFt: number,
): LngLat | null {
  if (roads.length === 0) return null;
  const inB = (p: LngLat) => booleanPointInPolygon([p.lng, p.lat], boundary);
  const eP = point([entry.lng, entry.lat]);
  const brng = turfBearing(point([hider.lng, hider.lat]), eP);
  const far = destination(eP, 5, brng, { units: 'kilometers' }); // 5 km ray
  const ray = lineString([
    [entry.lng, entry.lat],
    far.geometry.coordinates as [number, number],
  ]);
  const minM = minFt * METERS_PER_FOOT;

  // first road crossing ≥ minM from the entry, in bounds
  let best: { at: LngLat; d: number } | null = null;
  for (const road of roads) {
    let hits;
    try {
      hits = lineIntersect(ray, road);
    } catch {
      continue;
    }
    for (const h of hits.features) {
      const [lng, lat] = h.geometry.coordinates;
      const at = { lat, lng };
      const d = distance(eP, h, { units: 'meters' });
      if (d >= minM && inB(at) && (!best || d < best.d)) best = { at, d };
    }
  }
  if (best) return best.at;

  // fallback: where the ray crosses the boundary → nearest in-bounds road point
  let border: LngLat | null = null;
  try {
    const bx = lineIntersect(ray, boundary);
    let bestD = Infinity;
    for (const h of bx.features) {
      const [lng, lat] = h.geometry.coordinates;
      const d = distance(eP, h, { units: 'meters' });
      if (d > 1 && d < bestD) {
        bestD = d;
        border = { lat, lng };
      }
    }
  } catch {
    /* none */
  }
  if (!border) return null;
  const near = nearestRoadPoint(roads, border);
  if (near && inB(near.at)) return near.at;
  // last resort: pull the border point slightly inward toward the entry
  const pulled = destination(point([border.lng, border.lat]), 0.02, turfBearing(point([border.lng, border.lat]), eP), {
    units: 'kilometers',
  });
  const [lng, lat] = pulled.geometry.coordinates;
  const at = { lat, lng };
  return inB(at) ? at : border;
}
